"use client";

import { useRouter } from "next/navigation";
import { useTransition, type FormEvent } from "react";
import { useLocale } from "@/components/layout/locale-context";
import { IconCalendar, IconHash, IconPin, IconSearch, IconSuitcase } from "@/components/ui/icons";
import { Input, Select } from "@/components/ui/form-field";
import { Spinner, buttonClass } from "@/components/ui/button";
import type { Dictionary } from "@/i18n/dictionaries/tr";
import { parseQuery } from "../filters";
import { listingHref, paramsToRecord, type ListingNav } from "../listing-url";
import { cleanParams } from "./filter-form";
import { trackSearch } from "@/features/analytics/track";

type Option = { value: string; label: string };

// Ana sayfa araması → /turlar?bolge=…|kategori=…&ay=…&aile=… (filtre listesindeki parametrelerle aynı); yalnızca bölge,
// destinasyon ya da ürün ailesi seçiliyse doğrudan o filtrenin sayfası (/avrupa-turlari; listing-url.ts).
// JavaScript yoksa GET form; destinasyon seçimi "bolge:avrupa" / "kategori:paris-turlari" biçiminde
// gelir ve /turlar sayfası bunu tanımaz, bu yüzden iki ayrı alan kullanılır.
// "Tur kodu" (kod=…): liste sayfası sunucuda çözer — tam tur kodu → tur sayfası, kalkış kodu → tur sayfası + o kalkış
// (?kalkis=), kısmi kod → liste filtrelenir (diğer seçimlerle birlikte). Eşleştirme: features/tours/tour-code.ts
// JavaScript yoksa form /api/tur-kodu'ya gider (dil gizli alanda): kod çözülür, tur sayfasına ya da listeye HTTP 303.
export function HeroSearch({
  nav,
  t,
  regions,
  categories,
  months,
  families,
}: {
  /** Bu dildeki tur listesi adresi (/turlar, /en/tours) ve filtre sayfaları */
  nav: ListingNav;
  t: Dictionary["search"];
  regions: Option[];
  categories: Option[];
  months: Option[];
  families: Option[];
}) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const locale = useLocale();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const dest = (form.elements.namedItem("destinasyon") as HTMLSelectElement).value;
    const params = new URLSearchParams(cleanParams(form));
    // Analitik (onay varsa): search + kod girildiyse sonucu sonraki sayfada (tour_code_search)
    trackSearch("home_hero", params);
    params.delete("destinasyon");
    params.delete("dil");
    if (dest) {
      const [kind, value] = dest.split(":");
      params.set(kind === "bolge" ? "bolge" : "kategori", value);
    }
    startTransition(() => router.push(listingHref(parseQuery(paramsToRecord(params)).query, nav)));
  };

  return (
    <form
      action="/api/tur-kodu"
      method="get"
      onSubmit={onSubmit}
      aria-label={t.formAria}
      className="grid grid-cols-2 gap-3 rounded-xl bg-surface p-4 shadow-raised ring-1 ring-line lg:grid-cols-[1.35fr_1fr_1fr_1fr_auto] lg:items-end lg:gap-4 lg:p-5"
    >
      <input type="hidden" name="dil" value={locale} />
      <div className="col-span-2 grid gap-1.5 lg:col-span-1">
        <label htmlFor="hs-dest" className="flex items-center gap-1.5 text-xs font-bold tracking-wide text-ink uppercase">
          <IconPin size={15} className="text-accent" /> {t.where}
        </label>
        <Select id="hs-dest" name="destinasyon" defaultValue="">
          <option value="">{t.allDestinations}</option>
          <optgroup label={t.regionsGroup}>
            {regions.map((r) => (
              <option key={r.value} value={`bolge:${r.value}`}>
                {r.label}
              </option>
            ))}
          </optgroup>
          <optgroup label={t.destinationsGroup}>
            {categories.map((c) => (
              <option key={c.value} value={`kategori:${c.value}`}>
                {c.label}
              </option>
            ))}
          </optgroup>
        </Select>
      </div>
      <div className="grid min-w-0 gap-1.5">
        <label htmlFor="hs-ay" className="flex items-center gap-1.5 text-xs font-bold tracking-wide text-ink uppercase">
          <IconCalendar size={15} className="text-accent" /> {t.when}
        </label>
        <Select id="hs-ay" name="ay" defaultValue="">
          <option value="">{t.allDates}</option>
          {months.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </Select>
      </div>
      <div className="grid min-w-0 gap-1.5">
        <label htmlFor="hs-aile" className="flex items-center gap-1.5 text-xs font-bold tracking-wide text-ink uppercase">
          <IconSuitcase size={15} className="text-accent" /> {t.family}
        </label>
        <Select id="hs-aile" name="aile" defaultValue="">
          <option value="">{t.all}</option>
          {families.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </Select>
      </div>
      <div className="col-span-2 grid min-w-0 gap-1.5 lg:col-span-1">
        <label htmlFor="hs-kod" className="flex items-center gap-1.5 text-xs font-bold tracking-wide text-ink uppercase">
          <IconHash size={15} className="text-accent" /> {t.code}
        </label>
        <Input
          id="hs-kod"
          name="kod"
          type="text"
          placeholder={t.codePlaceholder}
          aria-describedby="hs-kod-hint"
          maxLength={40}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          enterKeyHint="search"
          className="not-placeholder-shown:font-semibold not-placeholder-shown:tracking-wide not-placeholder-shown:uppercase"
        />
        <span id="hs-kod-hint" className="sr-only">
          {t.codeHint}
        </span>
      </div>
      <button
        type="submit"
        disabled={pending}
        aria-busy={pending || undefined}
        className={buttonClass("cta", "md", "col-span-2 text-base! lg:col-span-1 lg:px-8!")}
      >
        {pending ? <Spinner size={20} /> : <IconSearch size={20} />} {pending ? t.pending : t.submit}
      </button>
    </form>
  );
}
